import type { MediaUnitFilters, CostRangeDimensionId } from '@ooh/contracts';
import { COST_RANGE_DIMENSIONS } from '@ooh/contracts';

/**
 * Numeric range field ids (CU-86cataqt9, extended to every displayed cost
 * column — the CU-86cataqt9 scope-reduction follow-up).
 *
 * Each entry backs one `range` FilterField (packages/ui). The URL persists
 * each pair as two SCALAR sidecar params (`<id>_min`/`<id>_max`), modeled on
 * the existing availabilityFrom/To sidecar pattern — see
 * `useRangeFilterParams` in `src/hooks/use-range-filter-params.ts`. The cost
 * dimensions are generated from COST_RANGE_DIMENSIONS (@ooh/contracts, the
 * single source of truth for the cost-record range dimensions) — every
 * INTERNAL COST is cost-gated (omitted when `canViewCosts` is false); every
 * CLIENT PRICE (catalogPrice/discountedPrice/lastMinutePrice/starterPrice/
 * clientProduction/clientInstallation/clientDismantling/clientAdditionalFees)
 * is never gated, whatever cost.view says — `cost.view` gates internal
 * costs, never client prices (permission-model correction, CU-86cbgnt38
 * round 4 — an earlier revision of this list cost-gated the first four).
 *
 * Neutral home (extracted from `features/media-units/lib/range-field-defs.ts`,
 * CU-86ca9k73a) so both the catalog (`features/media-units/lib/filter-fields.ts`)
 * and the offer units-tab (`features/offers/units-tab/lib/filter-fields.ts`)
 * share one source of truth instead of two copies — cross-feature import of
 * `features/media-units` from `features/offers` is forbidden
 * (WEB_ARCHITECTURE.md:36), same rationale as `src/lib/region-options.ts` and
 * `src/lib/media-type-options.ts`.
 *
 * The `minKey`/`maxKey` types are typed against `MediaUnitFilters` (the
 * catalog's filter shape) since both surfaces use the identical `<id>_min`/
 * `<id>_max` naming convention — `OfferUnitFilters` (@ooh/contracts) declares
 * the same keys independently.
 */
export interface RangeFieldDef {
  id: 'area' | 'width' | 'height' | 'audience' | CostRangeDimensionId;
  minKey: keyof MediaUnitFilters;
  maxKey: keyof MediaUnitFilters;
  unit?: string;
  /** When true, this dimension is a cost field — omitted unless canViewCosts. */
  costGated?: boolean;
  /**
   * Set only on the `rent` entry — omitted unless canViewRent, on top of
   * (not instead of) `costGated`, since rent is one of the internal costs
   * `cost.view` already covers and additionally requires the wildcard
   * `cost.view_rent` grant (field-visibility, CU-86cbgnt3z rent-visibility
   * split). Every other dimension leaves this undefined.
   */
  rentGated?: boolean;
}

const NON_COST_RANGE_FIELD_DEFS: readonly RangeFieldDef[] = [
  { id: 'area', minKey: 'area_min', maxKey: 'area_max', unit: 'm²' },
  { id: 'width', minKey: 'width_min', maxKey: 'width_max', unit: 'm' },
  { id: 'height', minKey: 'height_min', maxKey: 'height_max', unit: 'm' },
  { id: 'audience', minKey: 'audience_min', maxKey: 'audience_max' },
];

/**
 * Client price dimensions whose value is ALWAYS visible in the response
 * regardless of cost.view — mirrors the exclusion of these ids from
 * HIDDEN_COST_FIELD_IDS in apps/api/src/common/field-visibility.ts. Their
 * range filter must NOT be gated behind canViewCosts.
 * catalogPrice/discountedPrice/lastMinutePrice/starterPrice were corrected
 * into this set from the internal-cost set (permission-model correction,
 * CU-86cbgnt38 round 4) — they are client prices, not costs.
 */
export const CLIENT_FACING_COST_DIMENSION_IDS: ReadonlySet<CostRangeDimensionId> = new Set([
  'catalogPrice',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
]);

const COST_RANGE_FIELD_DEFS: readonly RangeFieldDef[] = COST_RANGE_DIMENSIONS.map((d) => ({
  id: d.id,
  minKey: `${d.id}_min` as keyof MediaUnitFilters,
  maxKey: `${d.id}_max` as keyof MediaUnitFilters,
  unit: 'PLN',
  costGated: !CLIENT_FACING_COST_DIMENSION_IDS.has(d.id),
  rentGated: d.id === 'rent',
}));

export const RANGE_FIELD_DEFS: readonly RangeFieldDef[] = [
  ...NON_COST_RANGE_FIELD_DEFS,
  ...COST_RANGE_FIELD_DEFS,
];
